import { useEffect, useMemo, useState } from "react";
import { Button } from "../../../components/ui/Button";
import { SpinnerIcon, WrenchIcon } from "../../../icons";
import { LeftArrow } from "../../../icons/arrows/leftArrow";
import { getVehicleDeliveryShowSummaryFn } from "../../../transactions/deliveryBooking/deliveryBooking";
import { useDeliveryFlowStore } from "../../../store/newVehicleDeliveryStore";
import { useAdditionalComments, useBooking, useOptionSelection } from "../../../hooks/react-query/useVehicleDelivery";
import { validateResponse, undoUntil, toUTCISOString } from "../deliveryUtils";
import { useOnlinePaymentInitit } from "../../../hooks/react-query/usePayment";
import { toast } from "sonner";
import { queryClient } from "../../../utils/queryClient";
import { QUERY_KEYS } from "../../../config/constants";

export const DeliveryBookingStepHeader = () => {
  
  // LOCAL VARIABLES:
  const carPlateno = useDeliveryFlowStore((s) => s.carPlateno);
  const uiStep = useDeliveryFlowStore((s) => s.uiStep);
  const flowId = useDeliveryFlowStore((s) => s.flowId);
  const flowStep = useDeliveryFlowStore((s) => s.flowStep);
  const selectedCalendarOption = useDeliveryFlowStore((s) => s.selectedCalendarOption);
  const interimVehiclePlate = useDeliveryFlowStore((s) => s.interimVehiclePlate);
  const hasOnlyCalendar = useDeliveryFlowStore((s) => s.hasOnlyCalendar);

  const setUiStep = useDeliveryFlowStore((s) => s.setUiStep);
  const setSelectedOption = useDeliveryFlowStore((s) => s.setSelectedOption);
  const setFlowStep = useDeliveryFlowStore((s) => s.setFlowStep);
  const setDeliveryAddress = useDeliveryFlowStore((s) => s.setDeliveryAddress);
  const isVehicleDeliverySelected = useDeliveryFlowStore((s) => s.isVehicleDeliverySelected);
  const setBookingCalendarOptions = useDeliveryFlowStore((s) => s.setBookingCalendarOptions);
  const setSelectedCalendarOption = useDeliveryFlowStore((s) => s.setSelectedCalendarOption);
  const setSummaryObject = useDeliveryFlowStore((s) => s.setSummaryObject);

  const {mutateAsync: optionSelect, isLoading: isOptionSelectLoading} = useOptionSelection()
  const {mutateAsync: bookDelivery, isLoading: isBookingLoading} = useBooking()
  const {mutateAsync: additionalComments, isLoading: isCommentsLoading} = useAdditionalComments()
  const {mutateAsync: paymentInit, isLoading: isPaymentLoading} = useOnlinePaymentInitit()

  const { VEHICLE_DELIVERY_INFO } = QUERY_KEYS;

  const stepTitles = {
    1 : "Τρόπος εξυπηρέτησης",
    2 : "Προγραμματισμός ραντεβού",
    3 : "Ολοκλήρωση",
  }
  
  // LOCAL STATE:
  const [enabledNext, setEnabledNext] = useState(false)
  const [isUndoLoading, setIsUndoLoading] = useState(false)
  const [isEntryLoading, setIsEntryLoading] = useState(false)
  // const [nextStepButtonText, setNextStepButtonText] = useState("Επόμενο")

  // FETCH DATA:
  
    // const isPickup = isVehicleDeliverySelected

    const nextStepButtonText = useMemo(() => {
      // if (isPickup && isLastStep) return "Πληρωμή & Προγραμματισμός";
      
      if (uiStep === 2) return "Προγραμματισμός";

      return "Επόμενο";
    }, [uiStep]);

  // this one checks about the enabled state of next button
  useEffect(()=> {
    if(uiStep === 1) {
      // Not something here to check. There will always be a preselection.
        setEnabledNext(true)
    }
    if(uiStep === 2) {
      if(!!selectedCalendarOption?.selectedDate && selectedCalendarOption?.selectedSlots?.length > 0) {
        setEnabledNext(true)
      } else setEnabledNext(false)
    }
  }, [isVehicleDeliverySelected, selectedCalendarOption?.selectedDate, selectedCalendarOption?.selectedSlots?.length, uiStep])

  // useEffect(() => {
  //   console.log(apiOptions)
  // },[apiOptions])

  // HANDLERS & FUNCTIONS

  const stepOneHandler = async () => {
    setSelectedOption()
    try{
      const optionPayload = {optionSelection: isVehicleDeliverySelected ? "HomeDelivery" : "GarageDelivery"}
      const optionResponse = await optionSelect({vehicleFlowId: flowId, payload:optionPayload})
      const isValidResponse = validateResponse(optionResponse)
      if(isValidResponse) {
        const bookingCalendarOptions = optionResponse?.inputs?.bookingCalendar?.options?.map((option) => {return {startTime: option?.StartTime, endTime:option?.EndTime}})

        // set New State based on the response
        setBookingCalendarOptions(bookingCalendarOptions)
        setSelectedCalendarOption({selectedDate:null, selectedSlots:[]})
        setFlowStep(optionResponse?.name)

        setUiStep(uiStep + 1)
      } else {
        toast({
          variant: 'destructive',
          description: optionResponse?.exceptions?.optionSelection?.[0] ?? "Προέκυψε κάποιο σφάλμα",
        });
      }
    } catch(e) {
      console.warn(e)
    }
  }

  const stepTwoHandler = async () => {
    try{
      const bookRes = await bookDelivery({
        payload: {
          bookingCalendar:toUTCISOString( selectedCalendarOption?.selectedSlots?.[0]?.date, selectedCalendarOption?.selectedSlots?.[0]?.start),
          interimVehicle: interimVehiclePlate || ""
        },
        vehicleFlowId: flowId,
      });

      if (validateResponse(bookRes)) {
        const commentsRes = await additionalComments({
          payload: {additionalComments:""},
          vehicleFlowId: flowId,
        });

        if (validateResponse(commentsRes)) {
          setFlowStep(commentsRes?.name)
          if(commentsRes?.pendingPayment) {
          const summaryRes = await getVehicleDeliveryShowSummaryFn(flowId)
            if (!validateResponse(summaryRes)) {
              const message = summaryRes?.exceptions?.[0] || 'Παρουσιάστηκε κάποιο πρόβλημα';
              toast({
                variant: 'destructive',
                description: message,
              });
              return;
            } else {
              if(summaryRes?.pendingPayment) {
                initiatePayment()
              } else {
                setUiStep(uiStep+1)
                setSummaryObject(summaryRes)
                queryClient.invalidateQueries({
                  queryKey: [VEHICLE_DELIVERY_INFO, carPlateno],
              });
              }
            }
          } else {
            setUiStep(uiStep+1)
            setSummaryObject(commentsRes)
            queryClient.invalidateQueries({
                queryKey: [VEHICLE_DELIVERY_INFO, carPlateno],
            });
          }
        } else {
          toast({
            variant: 'destructive',
            description: commentsRes?.exceptions?.plateno?.[0] ?? "Προέκυψε κάποιο σφάλμα",
          });
        }
      } else {
        console.warn(bookRes?.exceptions?.interimVehicle?.[0])
      }
    } catch(e) {
      console.warn(e)
    }
  }

  const initiatePayment = () => {
    try {
      paymentInit(
        {
          vehicleDeliveryFlowId: flowId,
          payment_type: 'vehicleDelivery',
        },
        {
          onSuccess: (response) => {
            if (response.exceptions) {
              if (response.exceptions.length === 0) {
                const data = JSON.parse(response.data);
                const url = data.url;
                url && window.location.replace(url);
              }
            }
          },
        }
      );
    } catch(e) {
      console.warn(e)
    }
  }

  const nextStep = async () => {
    if (uiStep < 3 && enabledNext) {
      if(hasOnlyCalendar && uiStep === 1) {
        setUiStep(2)
      } else if(!hasOnlyCalendar && uiStep === 1) {
        uiStep === 1 && stepOneHandler()
      }
      uiStep === 2 && stepTwoHandler()
    }
  };

  const prevStep = async () => {
    setIsUndoLoading(true)
    if(uiStep === 2){
      try{
        undoUntil({
          targetStep: "optionSelection",
          flowId: flowId,
          flowStep: flowStep,
          setFlowStep: setFlowStep
        })
        setBookingCalendarOptions([])
        setSelectedCalendarOption({selectedDate:null, selectedSlots:[]})
        setDeliveryAddress({street:"", number:"", area:"", zipCode:""})

        setUiStep(uiStep - 1)
      } catch (e) {
        console.warn(e)
      }
    }
    setIsUndoLoading(false)
  };


  return (
    <>
      {(isEntryLoading || 
        isUndoLoading || 
        isOptionSelectLoading || 
        isBookingLoading ||
        isCommentsLoading ||
        isPaymentLoading
      ) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20">
          {/* <Loader /> */}
        </div>
      )}
      <div className="flex flex-row justify-between items-center w-[90%] pb-4 mb-10 border-b-[0.5px] border-fadedgrey lg:mr-[45px]">
        <div className='flex flex-row gap-2 items-center' >
          {uiStep > 0 &&
          <>
            {uiStep > 1 && <Button className='h-full pl-1' onClick={prevStep}><LeftArrow className='cursor-pointer'/></Button>}
            <WrenchIcon className="stroke-black w-[18px] h-[18px]"/>
            <h4 className='font-bold'>{stepTitles[uiStep] || ""}</h4>
          </>}
        </div>
        <Button className={`flex flex-row gap-2 items-center justify-center text-white text-sm font-bold ${!enabledNext? "bg-fadedgrey" : "bg-[#D4002A]"} py-3 px-10 rounded-[6px]`} 
          disabled={!enabledNext} onClick={nextStep}>
            {nextStepButtonText}
            {(isEntryLoading || 
              isUndoLoading || 
              isOptionSelectLoading || 
              isBookingLoading ||
              isCommentsLoading ||
              isPaymentLoading
            ) && <SpinnerIcon className="w-4 h-4 fill-white" />}
        </Button>
      </div>
    </>
  );
};
